import { useCallback, useEffect, useState } from "react";
import { useDeprecationNotice } from "./DeprecationNoticeContext";
import { CautionIcon } from "./CautionIcon";
import "./DeprecationNotice.css";

const NEW_PORTAL_URL = "https://insights.magichealth.io/";
const CONTACT_EMAIL = "devina.sen@magichealth.io";
const RETIREMENT_DATE = new Date("2026-10-12T00:00:00");

const DeprecationNotice = () => {
  const notice = useDeprecationNotice();
  const [copied, setCopied] = useState(false);

  const retired = Date.now() >= RETIREMENT_DATE.getTime();

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(NEW_PORTAL_URL);
      setCopied(true);
    } catch (e) {
      /* clipboard unavailable — the address is on screen and linked anyway */
    }
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!notice || notice.collapsed) return null;

  return (
    <section className="deprecation-notice" aria-label="Service change notice">
      <span className="deprecation-notice__icon">
        <CautionIcon />
      </span>

      <p className="deprecation-notice__text">
        <strong>Service change — 12 October 2026.</strong>{" "}
        {retired ? (
          <>
            This portal has moved to{" "}
            <a href={NEW_PORTAL_URL} target="_blank" rel="noopener noreferrer">
              insights.magichealth.io
            </a>
            . Calls are processed exclusively in the new portal, and this URL
            will be deprecated after Oct 17th. Contact{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to learn
            more.
          </>
        ) : (
          <>
            This portal will move to{" "}
            <a href={NEW_PORTAL_URL} target="_blank" rel="noopener noreferrer">
              insights.magichealth.io
            </a>
            . Temporary log-in credentials for the new portal will be provided
            before the change. Calls will be processed exclusively in the new
            portal starting Oct 12th and this URL will be deprecated after Oct
            17th. Contact{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to learn
            more.
          </>
        )}
      </p>

      <span className="deprecation-notice__actions">
        <a
          className="deprecation-notice__primary"
          href={NEW_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open new portal
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>

        <button
          type="button"
          className="deprecation-notice__secondary"
          onClick={copyLink}
        >
          {copied ? "Copied" : "Copy link"}
        </button>

        <button
          type="button"
          className="deprecation-notice__close"
          onClick={notice.collapse}
          aria-label="Collapse notice"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </span>
    </section>
  );
};

export default DeprecationNotice;
